/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils/DateTimeUtils';

import { Button, Grid, Typography } from '@coursera/cds-core';

import type { DdpFieldsBelowFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import { ReadMoreToggle } from 'bundles/degree-description/components/shared/TextToggle';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { isExternalLink } from 'bundles/degree-description/utils/utils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

type EventCollectionType = NonNullable<DdpFieldsBelowFoldFragment['upcomingEventsCollection']>['items'];
// Don't want to pass __typename or sys as a prop
type EventType = Omit<NonNullable<EventCollectionType[0]>, '__typename' | 'sys'>;

type Props = EventType;

const styles = {
  cardContainer: css`
    border-radius: 8px;
    padding: 40px 32px;
    background-color: rgb(0 86 210 / 8%);
  `,
  description: css`
    display: block;
    min-height: 48px;
    max-height: fit-content;
    margin-bottom: 12px;
    margin-top: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  `,
};

export const EventCard = ({ eventDate, recurrenceFrequency, eventDescription, ctaTitle, ctaUrl }: Props) => {
  const theme = useDegreeDescriptionPageTheme();
  const { trackClickButton } = useDegreeDescriptionEventingContext();

  const handleClick = () => {
    if (ctaUrl && ctaTitle) {
      trackClickButton('degree_register_event', 'events', ctaUrl, ctaTitle);
    }
  };

  return (
    <Grid item xs={12} sm={6} md={4}>
      <div css={styles.cardContainer}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography variant="h1semibold" component="h2" css={theme.mobileFontSizeStyle?.header}>
          {eventDate
            ? formatDateTimeDisplay(eventDate, MOMENT_FORMATS.LONG_DATE_ONLY_NO_YEAR_DISPLAY)
            : recurrenceFrequency}
        </Typography>
        {!!eventDescription && <ReadMoreToggle text={eventDescription} />}
        {ctaUrl && ctaTitle && (
          <Button
            component={TrackedLink2}
            onClick={handleClick}
            variant="secondary"
            href={ctaUrl}
            size="small"
            target={isExternalLink(ctaUrl) ? '_blank' : undefined}
            rel={isExternalLink(ctaUrl) ? 'noopener noreferrer' : undefined}
            trackingName="register_event"
          >
            {ctaTitle}
          </Button>
        )}
      </div>
    </Grid>
  );
};
